import { useLayoutEffect, useRef } from 'react';
import type { MutableRefObject, RefObject } from 'react';

import type { AppDispatch } from 'features/app/store/store';
import type {
  CaptionLineForTurns,
  CaptionTurn,
} from 'features/call/call-captions/helpers';
import {
  countVisualLines,
  measureFirstLineCharCount,
  snapToWordBoundary,
} from 'features/call/call-captions/helpers';
import {
  incrementCaptionTrim,
  pruneLines,
} from 'features/call/call-captions/store';

interface UseCaptionPruningParams {
  dispatch: AppDispatch;
  visibleCaptions: CaptionLineForTurns[];
  renderedTurns: CaptionTurn[];
  trimOffsets: Record<string, number>;
  containerRef: RefObject<HTMLElement | null>;
  captionTextRefs: MutableRefObject<Record<string, HTMLElement | null>>;
  captionsEnabled: boolean;
  resizeTick: number;
  onPrunedToEmpty?: () => void;
}

const buildCaptionCycleKey = (
  newestCaption: CaptionLineForTurns | undefined,
  resizeTick: number
): string =>
  [
    newestCaption?.resultId ?? 'none',
    newestCaption?.text.length ?? 0,
    resizeTick,
  ].join(':');

const buildLineTrimCycleKey = ({
  captionCycleKey,
  oldestTurnKey,
  trimOffsets,
}: {
  captionCycleKey: string;
  oldestTurnKey: string;
  trimOffsets: Record<string, number>;
}): string =>
  [captionCycleKey, oldestTurnKey, trimOffsets[oldestTurnKey] ?? 0].join(':');

export const useCaptionPruning = ({
  dispatch,
  visibleCaptions,
  renderedTurns,
  trimOffsets,
  containerRef,
  captionTextRefs,
  captionsEnabled,
  resizeTick,
  onPrunedToEmpty,
}: UseCaptionPruningParams): void => {
  const lastPrunedCycleRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    if (!captionsEnabled || !visibleCaptions.length) {
      return;
    }

    const container = containerRef.current;

    if (!container) {
      return;
    }

    const shouldPruneByOverflow =
      container.scrollHeight > container.clientHeight;

    if (!shouldPruneByOverflow) {
      lastPrunedCycleRef.current = null;
      return;
    }

    const firstTurn = renderedTurns.at(0);

    if (!firstTurn) {
      return;
    }

    const newestCaption = visibleCaptions.at(-1);
    const captionCycleKey = buildCaptionCycleKey(newestCaption, resizeTick);

    if (shouldPruneByOverflow) {
      const textElement = captionTextRefs.current[firstTurn.key];

      if (textElement) {
        const visualLineCount = countVisualLines(textElement);

        if (visualLineCount > 1) {
          const lineTrimCycleKey = buildLineTrimCycleKey({
            captionCycleKey,
            oldestTurnKey: firstTurn.key,
            trimOffsets,
          });

          if (lastPrunedCycleRef.current !== lineTrimCycleKey) {
            const visibleText = textElement.textContent ?? '';
            const rawCharCount = measureFirstLineCharCount(textElement);
            const charCount = snapToWordBoundary(visibleText, rawCharCount);

            if (charCount > 0) {
              dispatch(
                incrementCaptionTrim({ turnKey: firstTurn.key, charCount })
              );
              lastPrunedCycleRef.current = lineTrimCycleKey;
            }
          }

          return;
        }
      }
    }

    if (lastPrunedCycleRef.current === captionCycleKey) {
      return;
    }

    const turnIdsToPrune = new Set(firstTurn.resultIds);
    const idsToKeep = visibleCaptions
      .filter((line) => !turnIdsToPrune.has(line.resultId))
      .map((line) => line.resultId);

    if (idsToKeep.length === visibleCaptions.length) {
      return;
    }

    if (idsToKeep.length === 0) {
      onPrunedToEmpty?.();
    }

    dispatch(pruneLines(idsToKeep));
    lastPrunedCycleRef.current = captionCycleKey;
  }, [
    captionTextRefs,
    captionsEnabled,
    containerRef,
    dispatch,
    onPrunedToEmpty,
    renderedTurns,
    resizeTick,
    trimOffsets,
    visibleCaptions,
  ]);
};
